// src/utils/colorHelpers.js
/**
 * Shared color helper functions for performance metrics
 * Ensures consistent color coding across the application
 */

/**
 * Get color for CSAT score (1-5 scale)
 * @param {number} score - CSAT score between 0-5
 * @returns {string} Hex color code
 */
export const getCsatColor = (score) => {
  if (score >= 4) return '#03DAC6'; // Green for excellent (4-5)
  if (score >= 3) return '#FFCF44'; // Yellow for good (3-4)
  return '#CF6679'; // Red for needs improvement (0-3)
};

/**
 * Get color for DIR score (-10 to +10 scale, typically -5 to +5)
 * @param {number} value - DIR score
 * @returns {string} Hex color code
 */
export const getDirColor = (value) => {
  if (value > 5) return '#03DAC6'; // Green for exceptional
  if (value > 0) return '#57DEC8'; // Light green for positive
  if (value === 0) return '#FFCF44'; // Yellow for neutral/replacement level
  if (value > -5) return '#E57C73'; // Light red for negative
  return '#CF6679'; // Red for very negative
};

/**
 * Get color for utilization percentage
 * @param {number} percentage - Utilization percentage (0-100+)
 * @returns {string} Hex color code
 */
export const getUtilizationColor = (percentage) => {
  if (percentage >= 85) return '#03DAC6'; // Green for optimal utilization
  if (percentage >= 70) return '#FFCF44'; // Yellow for acceptable
  return '#CF6679'; // Red for low utilization
};

/**
 * Get color for CSOT percentage
 * @param {number} percentage - CSOT percentage (0-100+)
 * @returns {string} Hex color code
 */
export const getCsotColor = (percentage) => {
  // Green for optimal range (80-110%)
  if (percentage >= 80 && percentage <= 110) return '#03DAC6';
  // Yellow for caution range (50-79.9%)
  if (percentage >= 50 && percentage < 80) return '#FFCF44';
  // Red for too low (<50%) or overcommitted (>110%)
  return '#CF6679';
};

/**
 * Get color for confidence level (0-1 scale)
 * @param {number} confidence - Confidence level between 0-1
 * @returns {string} Hex color code
 */
export const getConfidenceColor = (confidence) => {
  if (confidence >= 0.8) return '#03DAC6'; // Green for high confidence
  if (confidence >= 0.6) return '#FFCF44'; // Yellow for medium confidence
  return '#CF6679'; // Red for low confidence
};

/**
 * Get color for hours per week
 * @param {number|string} hours - Hours per week
 * @returns {string} Hex color code
 */
export const getHoursColor = (hours) => {
  const numHours = Number(hours);

  // Green for normal hours (35-45)
  if (numHours >= 35 && numHours <= 45) return '#03DAC6';
  // Yellow for medium hours (20-35)
  if (numHours >= 20 && numHours < 35) return '#FFCF44';
  // Red for very low (<20) or high (>45) hours
  return '#CF6679';
};

/**
 * Get color based on generic score (0-10 scale)
 * @param {number} score - Score between 0-10
 * @returns {string} Hex color code
 */
export const getScoreColor = (score) => {
  if (score >= 8) return '#03DAC6'; // Green for high scores
  if (score >= 6) return '#FFCF44'; // Yellow for medium scores
  return '#CF6679'; // Red for low scores
};

/**
 * Theme colors for consistent application styling
 */
export const THEME_COLORS = {
  success: '#03DAC6',
  successLight: '#57DEC8',
  warning: '#FFCF44',
  error: '#CF6679',
  errorLight: '#E57C73',
  primary: '#BB86FC',
  info: '#64B5F6',
  background: {
    paper: '#1A1A1A',
    darker: '#0A0A0A',
    elevated: 'rgba(255, 255, 255, 0.05)'
  }
};
